import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"
const API_URL = import.meta.env.VITE_API_URL || ''

/* ─── Utility ─────────────────────────────────────────────────────────────── */
function clamp(val, min, max) { return Math.min(Math.max(val, min), max) }

import { GLOBAL_CURRENCIES, useCurrencies } from '../context/CurrencyContext'

function fmtDisplay(val, currency) {
  if (!val) return '';
  const numVal = Number(val);
  if (isNaN(numVal)) return val;
  const cur = GLOBAL_CURRENCIES.find(c => c.code === currency) || GLOBAL_CURRENCIES[0]
  const converted = numVal * cur.rate;
  if (converted >= 1_000_000) return `${cur.code} ${(converted / 1_000_000).toFixed(converted % 1_000_000 < 50_000 ? 0 : 2)}M`
  if (converted >= 1_000)     return `${cur.code} ${(converted / 1_000).toFixed(0)}K`
  return `${cur.code} ${Math.round(converted).toLocaleString()}`
}

function fmtSize(val, unit) {
  if (!val) return '';
  const numVal = Number(val);
  if (isNaN(numVal)) return val;
  if (unit === 'm2') {
    return `${Math.round(numVal * 0.092903).toLocaleString()} sq.m`;
  }
  return `${Math.round(numVal).toLocaleString()} sq.ft`;
}

/* ─── Progress Bar ────────────────────────────────────────────────────────── */
function ProgressBar({ value }) {
  const pct = clamp(value ?? 0, 0, 100)
  return (
    <div style={{ width: '100%', background: 'rgba(0,46,83,0.08)', borderRadius: '99px', height: '8px', overflow: 'hidden' }}>
      <div style={{
        height: '100%', borderRadius: '99px',
        width: `${pct}%`,
        background: `linear-gradient(90deg, ${GOLD}, #e8c97a)`,
        transition: 'width 1s ease',
      }} />
    </div>
  )
}

/* ─── Gallery Lightbox ────────────────────────────────────────────────────── */
function Gallery({ images }) {
  const [active, setActive] = useState(0)
  const [lightbox, setLightbox] = useState(false)
  const [lbIdx, setLbIdx] = useState(0)

  // Listen for Escape and Arrow keys when lightbox is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightbox(false)
      if (e.key === 'ArrowRight') setLbIdx(i => (i + 1) % images.length)
      if (e.key === 'ArrowLeft') setLbIdx(i => (i - 1 + images.length) % images.length)
    }
    if (lightbox) window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightbox, images.length])

  if (!images || images.length === 0) return null

  const mainImg = images[active]

  const openLightbox = (i) => { setLbIdx(i); setLightbox(true) }
  const closeLightbox = () => setLightbox(false)
  const lbPrev = (e) => { e.stopPropagation(); setLbIdx(i => (i - 1 + images.length) % images.length) }
  const lbNext = (e) => { e.stopPropagation(); setLbIdx(i => (i + 1) % images.length) }

  // Calculate the looping thumbnails (max 3)
  const numThumbnails = Math.min(3, images.length - 1)
  const thumbnails = []
  for (let i = 1; i <= numThumbnails; i++) {
    const idx = (active + i) % images.length
    thumbnails.push({ idx, img: images[idx] })
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', height: '100%' }}>
        {/* Main large image */}
        <div
          style={{ flex: 1, minHeight: 0, position: 'relative', cursor: 'pointer', borderRadius: '14px', overflow: 'hidden' }}
          onClick={() => openLightbox(active)}
        >
          <img src={mainImg.url} alt={mainImg.label} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,20,40,0)', transition: 'background 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,20,40,0.15)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,20,40,0)'}
          />
          <div style={{ position: 'absolute', bottom: '12px', right: '12px', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '12px', fontFamily: FONT, padding: '4px 10px', borderRadius: '6px' }}>
            ⛶ View Full
          </div>
        </div>

        {/* Thumbnails row below */}
        {thumbnails.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${thumbnails.length}, 1fr)`, gap: '8px', height: '120px' }}>
            {thumbnails.map(({ idx, img }, i) => (
              <div
                key={idx}
                onClick={() => setActive(idx)}
                style={{
                  borderRadius: '10px', overflow: 'hidden', cursor: 'pointer',
                  position: 'relative',
                }}
              >
                <img src={img.url} alt={img.label} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                
                {/* "+X More" overlay on the last thumbnail if there are hidden images */}
                {i === thumbnails.length - 1 && images.length > numThumbnails + 1 && (
                  <div
                    onClick={e => { e.stopPropagation(); openLightbox(active) }}
                    style={{
                      position: 'absolute', inset: 0, background: 'rgba(0,20,40,0.65)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontFamily: FONT, fontWeight: 700, fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    +{images.length - (numThumbnails + 1)} More
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={closeLightbox}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.92)', display: 'flex',
            flexDirection: 'column',
          }}
        >
          <style>{`
            .hide-scroll::-webkit-scrollbar { display: none; }
            .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
          `}</style>
          <button onClick={closeLightbox} style={{ position: 'absolute', top: '24px', right: '24px', background: 'none', border: 'none', color: '#fff', fontSize: '32px', cursor: 'pointer', lineHeight: 1, zIndex: 10 }}>✕</button>
          
          <button onClick={lbPrev} style={{ position: 'absolute', top: '50%', left: '24px', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', fontSize: '28px', cursor: 'pointer', padding: '12px 18px', borderRadius: '10px' }}>‹</button>
          <button onClick={lbNext} style={{ position: 'absolute', top: '50%', right: '24px', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', fontSize: '28px', cursor: 'pointer', padding: '12px 18px', borderRadius: '10px' }}>›</button>
          
          {/* Main Image Area - Flexible Center */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, padding: '40px 80px' }}>
            <img
              src={images[lbIdx]?.url}
              alt={images[lbIdx]?.label}
              style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
            />
          </div>
            
          {/* Fixed Bottom Thumbnails Area */}
          <div style={{ height: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', paddingBottom: '24px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <div className="hide-scroll" style={{ display: 'flex', gap: '8px', overflowX: 'auto', overflowY: 'hidden', maxWidth: '90vw', paddingBottom: '4px' }}>
              {images.map((img, i) => (
                <img 
                  key={i} 
                  src={img.url} 
                  onClick={(e) => { e.stopPropagation(); setLbIdx(i); }}
                  style={{ 
                    height: '60px', width: '90px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer', flexShrink: 0,
                    border: lbIdx === i ? `2px solid ${GOLD}` : '2px solid transparent',
                    opacity: lbIdx === i ? 1 : 0.6,
                    transition: 'all 0.2s'
                  }} 
                />
              ))}
            </div>

            <div style={{ color: 'rgba(255,255,255,0.6)', fontFamily: FONT, fontSize: '13px' }}>
              {lbIdx + 1} / {images.length} — {images[lbIdx]?.label}
            </div>
          </div>

        </div>
      )}
    </>
  )
}

function FloorPlans({ plans, onOpenBrochure }) {
  const { t } = useLanguage()
  const { currencies } = useCurrencies()
  const [displayUnit, setDisplayUnit] = useState('sqft')
  const [displayCurrency, setDisplayCurrency] = useState('AED')

  if (!plans || plans.length === 0) return null

  const translateUnitType = (str) => {
    if (!str) return 'Unit';
    let val = str;
    ['Apartment', 'Townhouse', 'Villa', 'Penthouse', 'Mansion', 'Duplex', 'Plot'].forEach(type => {
      if (val.includes(type)) val = val.replace(type, t(`offplanDetail.types.${type}`) !== `offplanDetail.types.${type}` ? t(`offplanDetail.types.${type}`) : type);
    });
    if (val.includes('Bedroom')) val = val.replace('Bedroom', t('offplanDetail.beds.br'));
    if (val.includes('Studio')) val = val.replace('Studio', t('offplanDetail.beds.studio'));
    return val;
  }

  return (
    <div style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,46,83,0.04)' }}>
      {/* Filters Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: NAVY }}>{t('offplanExtra.areaUnit', 'Area Unit')}:</div>
          <div style={{ display: 'flex', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <button onClick={() => setDisplayUnit('sqft')} style={{ padding: '6px 12px', border: 'none', background: displayUnit === 'sqft' ? NAVY : 'transparent', color: displayUnit === 'sqft' ? '#fff' : '#64748b', fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>Sq.ft</button>
            <button onClick={() => setDisplayUnit('m2')} style={{ padding: '6px 12px', border: 'none', background: displayUnit === 'm2' ? NAVY : 'transparent', color: displayUnit === 'm2' ? '#fff' : '#64748b', fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>Sq.m</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: NAVY }}>{t('offplanExtra.currency', 'Currency')}:</div>
          <select value={displayCurrency} onChange={(e) => setDisplayCurrency(e.target.value)} style={{ padding: '6px 32px 6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontFamily: FONT, fontSize: '13px', color: NAVY, fontWeight: 600, outline: 'none', cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23002E53' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}>
            {currencies.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
          </select>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', textAlign: 'left', fontFamily: FONT }}>
          <thead>
            <tr style={{ background: NAVY, color: '#fff' }}>
              <th style={{ padding: '16px 24px', fontWeight: 600, fontSize: '13px' }}>{t('offplanDetail.propertyTypes')}</th>
              <th style={{ padding: '16px 24px', fontWeight: 600, fontSize: '13px' }}>{t('offplanExtra.units')}</th>
              <th style={{ padding: '16px 24px', fontWeight: 600, fontSize: '13px' }}>{t('offplanExtra.sizeRange')}</th>
              <th style={{ padding: '16px 24px', fontWeight: 600, fontSize: '13px' }}>{t('offplanDetail.startingPrice')}</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p, i) => {
              // Handle size display
              let sizeDisplay = '-';
              if (p.minSize && p.maxSize && p.minSize !== p.maxSize) {
                sizeDisplay = `${fmtSize(p.minSize, displayUnit)} ${t('offplanExtra.to', 'up to')} ${fmtSize(p.maxSize, displayUnit)}`;
              } else if (p.minSize) {
                sizeDisplay = fmtSize(p.minSize, displayUnit);
              } else if (p.sizeRange) {
                sizeDisplay = p.sizeRange; // fallback legacy string
              }

              // Handle price display
              let priceDisplay = '-';
              if (p.minPrice || (p.startingPrice && !isNaN(Number(p.startingPrice)))) {
                const numericPrice = Number(p.minPrice || p.startingPrice);
                priceDisplay = `${t('offplanExtra.from', 'From')} ${fmtDisplay(numericPrice, displayCurrency)}`;
              } else if (p.startingPrice) {
                // fallback legacy string, optionally replace 'AED' and 'From'
                priceDisplay = String(p.startingPrice).replace('From', t('offplanExtra.from', 'From')).replace('AED', displayCurrency === 'AED' ? 'AED' : '');
              }

              return (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ padding: '16px 24px', color: '#334155', fontSize: '14px' }}>{translateUnitType(p.unitType || p.label)}</td>
                  <td style={{ padding: '16px 24px', color: '#64748b', fontSize: '14px' }}>{p.units || '-'}</td>
                  <td style={{ padding: '16px 24px', color: '#64748b', fontSize: '14px' }}>{sizeDisplay}</td>
                  <td style={{ padding: '16px 24px', color: '#64748b', fontSize: '14px' }}>{priceDisplay}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div style={{ padding: '20px', textAlign: 'center', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <button onClick={onOpenBrochure} style={{ background: 'transparent', color: NAVY, border: `2px solid ${NAVY}`, padding: '10px 24px', borderRadius: '8px', fontFamily: FONT, fontWeight: 700, fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.background = NAVY; e.currentTarget.style.color = '#fff' }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = NAVY }}
        >
          📄 {t('offplanDetail.dlFloorPlans')}
        </button>
      </div>
    </div>
  )
}

function PaymentTimeline({ steps }) {
  const { t } = useLanguage()
  if (!steps || steps.length === 0) return null
  const total = steps.reduce((sum, s) => sum + (s.percentage || 0), 0)

  return (
    <div style={{ position: 'relative' }}>
      {/* Connecting line */}
      <div style={{ position: 'absolute', left: '20px', top: '20px', bottom: '20px', width: '2px', background: `linear-gradient(to bottom, ${GOLD}, rgba(197,160,89,0.2))` }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
        {steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', paddingBottom: i < steps.length - 1 ? '20px' : '0' }}>
            {/* Dot */}
            <div style={{ flexShrink: 0, width: '40px', height: '40px', borderRadius: '50%', background: i === 0 ? GOLD : i === steps.length - 1 ? NAVY : '#fff', border: `3px solid ${i === 0 ? GOLD : NAVY}`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <span style={{ fontFamily: FONT, fontSize: '11px', fontWeight: 800, color: (i === 0 || i === steps.length - 1) ? '#fff' : NAVY }}>
                {step.percentage}%
              </span>
            </div>

            {/* Content */}
            <div style={{ flex: 1, paddingTop: '8px' }}>
              <div style={{ fontFamily: FONT, fontSize: '14px', fontWeight: 700, color: NAVY, marginBottom: '2px' }}>
                {(() => {
                  let m = step.milestone;
                  if (m.includes('Booking')) m = m.replace('Booking', t('offplanExtra.booking', 'Booking'));
                  if (m.includes('Installment')) m = m.replace('Installment', t('offplanExtra.installment', 'Installment'));
                  if (m.includes('Handover')) m = m.replace('Handover', t('offplanExtra.handover', 'Handover'));
                  return m;
                })()}
              </div>
              <div style={{ fontFamily: FONT, fontSize: '12px', color: '#6b7a8d' }}>
                {step.percentage}% {t('offplanExtra.ofTotalPayment', 'of total payment')}
              </div>
              {/* Mini bar */}
              <div style={{ marginTop: '8px', background: 'rgba(0,46,83,0.06)', borderRadius: '99px', height: '5px', width: '80%', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(step.percentage / total) * 100}%`, background: GOLD, borderRadius: '99px' }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function AmenitiesGrid({ amenities }) {
  const { t } = useLanguage()
  if (!amenities || amenities.length === 0) return null

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '24px' }}>
      {amenities.map((a, i) => {
        const amenityStr = typeof a === 'string' ? a : a.name
        if (!amenityStr) return null
        const translatedStr = t(`amenities.${amenityStr}`) !== `amenities.${amenityStr}` ? t(`amenities.${amenityStr}`) : amenityStr;
        const icon = (typeof a === 'object' && a.icon) ? a.icon : '✦'
        
        return (
          <div key={i} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px',
            background: '#fff', padding: '24px', borderRadius: '16px',
            border: '1px solid rgba(0,46,83,0.04)', boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
            transition: 'all 0.3s ease', cursor: 'default',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,46,83,0.06)' }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.02)' }}
          >
            <div style={{ fontSize: '32px', lineHeight: 1, marginBottom: '4px' }}>
              {icon}
            </div>
            <span style={{ fontFamily: FONT, fontSize: '15px', fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
              {translatedStr}
            </span>
          </div>
        )
      })}
    </div>
  )
}

/* ─── Skeleton ────────────────────────────────────────────────────────────── */
function DetailSkeleton() {
  const shimmer = { animation: 'shim 1.5s infinite', background: 'linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%)', backgroundSize: '200% 100%' }
  return (
    <div style={{ maxWidth: '1200px', margin: '100px auto 60px', padding: '0 20px', fontFamily: FONT }}>
      <style>{`@keyframes shim { 0%{ background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '48px' }}>
        <div style={{ ...shimmer, borderRadius: '14px', height: '380px' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[0,1,2,3].map(i => <div key={i} style={{ ...shimmer, borderRadius: '10px', flex: 1 }} />)}
        </div>
      </div>
      {[200, 120, 80].map((h, i) => <div key={i} style={{ ...shimmer, borderRadius: '10px', height: `${h}px`, marginBottom: '20px' }} />)}
    </div>
  )
}

/* ─── Section Wrapper ─────────────────────────────────────────────────────── */
function Section({ title, badge, children, bg = '#fff', id }) {
  return (
    <div id={id} style={{ background: bg, padding: '64px 20px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {(title || badge) && (
          <div style={{ marginBottom: '36px' }}>
            {badge && (
              <span style={{
                display: 'inline-block', background: `${GOLD}18`, color: GOLD,
                fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
                padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
                border: `1px solid ${GOLD}35`, marginBottom: '10px',
              }}>{badge}</span>
            )}
            {title && (
              <h2 style={{ fontFamily: FONT, fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: 700, color: NAVY, margin: 0 }}>
                {title}
              </h2>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

/* ─── Brochure Modal ──────────────────────────────────────────────────────── */
function BrochureModal({ project, type = 'Brochure', onClose }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const res = await fetch('/api/adminOffplan/brochure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: project.id,
          projectName: project.name,
          type,
          ...formData
        })
      })
      if (!res.ok) throw new Error('Failed to submit')
      setSuccess(true)
    } catch {
      alert('Error submitting request. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,18,36,0.75)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
    >
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '440px', padding: '32px', position: 'relative', boxShadow: '0 40px 100px rgba(0,0,0,0.4)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7a8d' }}>✕</button>
        
        {success ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <h3 style={{ fontFamily: FONT, color: NAVY, marginBottom: '8px' }}>Request Received</h3>
            <p style={{ fontFamily: FONT, fontSize: '14px', color: '#6b7a8d' }}>Thank you. One of our specialists will email you the brochure and floor plans shortly.</p>
            <button onClick={onClose} style={{ marginTop: '20px', padding: '10px 24px', background: NAVY, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT, fontWeight: 600 }}>Close</button>
          </div>
        ) : (
          <>
            <h3 style={{ fontFamily: FONT, color: NAVY, fontSize: '20px', marginBottom: '8px', marginTop: 0 }}>Download {type}</h3>
            <p style={{ fontFamily: FONT, fontSize: '13px', color: '#6b7a8d', marginBottom: '24px' }}>Fill in your details to receive the full project {type.toLowerCase()} for {project.name}.</p>
            
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: NAVY, marginBottom: '6px' }}>Full Name *</label>
                <input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontFamily: FONT, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: NAVY, marginBottom: '6px' }}>Email Address *</label>
                <input required type="email" pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}" title="Please enter a valid email address" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontFamily: FONT, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: NAVY, marginBottom: '6px' }}>Phone Number</label>
                <input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', fontFamily: FONT, boxSizing: 'border-box' }} />
              </div>
              <button type="submit" disabled={loading} style={{ padding: '14px', background: GOLD, color: '#fff', border: 'none', borderRadius: '8px', fontFamily: FONT, fontWeight: 700, fontSize: '14px', cursor: loading ? 'not-allowed' : 'pointer', marginTop: '8px' }}>
                {loading ? 'Submitting...' : `Request ${type}`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

/* ─── Main Page ───────────────────────────────────────────────────────────── */
export default function OffplanDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useLanguage()

  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [stickyVisible, setStickyVisible] = useState(false)
  const [brochureModalType, setBrochureModalType] = useState(null)

  // Sticky CTA appears after scrolling 400px
  useEffect(() => {
    const onScroll = () => setStickyVisible(window.scrollY > 400)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Fetch project detail
  useEffect(() => {
    if (!id) return
    setLoading(true)
    setError(null)
    fetch(`${API_URL}/api/offplan/${id}`)
      .then(res => {
        if (res.status === 404) throw new Error('not_found')
        if (!res.ok) throw new Error(`Server responded with ${res.status}`)
        return res.json()
      })
      .then(data => { setProject(data); setLoading(false) })
      .catch(err => {
        console.error('[OffplanDetail] fetch error:', err)
        setError(err.message === 'not_found' ? 'not_found' : 'error')
        setLoading(false)
      })
  }, [id])

  if (loading) {
    return (
      <div style={{ fontFamily: FONT }}>
        <Navbar />
        <DetailSkeleton />
        <Footer />
      </div>
    )
  }

  if (error === 'not_found') {
    return (
      <div style={{ fontFamily: FONT }}>
        <Navbar />
        <div style={{ maxWidth: '600px', margin: '120px auto', padding: '0 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>🏚️</div>
          <h2 style={{ fontFamily: FONT, fontWeight: 700, color: NAVY, marginBottom: '10px' }}>Project Not Found</h2>
          <p style={{ color: '#6b7a8d', fontFamily: FONT, marginBottom: '28px' }}>This project may no longer be available or the link may be incorrect.</p>
          <button onClick={() => navigate('/offplan')} style={{ background: GOLD, color: '#fff', border: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '14px', padding: '13px 32px', borderRadius: '10px', cursor: 'pointer' }}>
            Browse All Projects
          </button>
        </div>
        <Footer />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ fontFamily: FONT }}>
        <Navbar />
        <div style={{ maxWidth: '600px', margin: '120px auto', padding: '0 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ fontFamily: FONT, fontWeight: 700, color: NAVY, marginBottom: '10px' }}>Failed to Load Project</h2>
          <p style={{ color: '#6b7a8d', fontFamily: FONT, marginBottom: '28px' }}>There was a problem fetching this project's details. Please try again shortly.</p>
          <button onClick={() => window.location.reload()} style={{ background: GOLD, color: '#fff', border: 'none', fontFamily: FONT, fontWeight: 700, fontSize: '14px', padding: '13px 32px', borderRadius: '10px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
        <Footer />
      </div>
    )
  }

  const p = project

  // ── Smart display strings from structured admin data ──────────────────────
  // propertyTypes: ['Apartment', 'Townhouse']
  // unitOptions:   { Apartment: ['1 Bedroom', '2 Bedroom'], Townhouse: ['3 Bedroom'] }
  const propertyTypes = p.propertyTypes || []
  const unitOptions   = p.unitOptions   || {}

  // Overall property types string: "Apartments and Townhouses"
  const propertyTypesStr = propertyTypes.length > 0
    ? propertyTypes.filter(t_str => t_str !== 'Retail').map(t_str => t(`offplanDetail.types.${t_str}`) !== `offplanDetail.types.${t_str}` ? t(`offplanDetail.types.${t_str}`) : t_str).join(', ')
    : (p.type ? (t(`offplanDetail.types.${p.type}`) !== `offplanDetail.types.${p.type}` ? t(`offplanDetail.types.${p.type}`) : p.type) : 'Residential')

  // Per-type unit option strings for the detail rows
  // e.g. { Apartment: "1, 2 & 3 Bedroom", Townhouse: "3 Bedroom" }
  const unitOptionStrings = {}
  Object.entries(unitOptions).forEach(([type, opts]) => {
    if (!opts || opts.length === 0) return
    const sorted = [...opts].sort((a, b) => {
      const aNum = parseInt(a); const bNum = parseInt(b)
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
      return a.localeCompare(b)
    })
    // Format as "1, 2 & 3 Bedroom" (studio stays as-is)
    const bedroomNums = sorted.filter(o => o !== 'Studio' && /\d+\s+Bedroom/.test(o)).map(o => o.replace(' Bedroom', ''))
    const hasStudio = sorted.includes('Studio')
    let str = ''
    if (hasStudio && bedroomNums.length > 0) {
      str = `Studio, ${bedroomNums.join(', ')} Bedroom`
    } else if (hasStudio) {
      str = 'Studio'
    } else if (bedroomNums.length > 0) {
      const last = bedroomNums.pop()
      str = bedroomNums.length > 0 ? `${bedroomNums.join(', ')} & ${last} Bedroom` : `${last} Bedroom`
    } else {
      str = sorted.join(', ')
    }
    unitOptionStrings[type] = str
  })

  // Legacy fallback for old records that used the free-text `type` field
  const unitOptionsStr = Object.values(unitOptionStrings).join(' / ') || null

  // Whatsapp enquiry link
  const waText = encodeURIComponent(`Hi, I'm interested in the off-plan project "${p.name}"${p.area ? ` in ${p.area}` : ''}. Could you please share more details?`)
  const waLink = `https://wa.me/971585815757?text=${waText}`

  return (
    <div style={{ fontFamily: FONT }}>
      <Navbar />

      {/* Global styles */}
      <style>{`
        @media (min-width: 901px) {
          .mobile-sticky-bar { display: none !important; }
        }
        @media (max-width: 768px) {
          .fp-grid { grid-template-columns: 1fr !important; }
          .detail-hero-grid { grid-template-columns: 1fr !important; }
          .detail-stats-row { grid-template-columns: repeat(2,1fr) !important; }
          .amenities-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 900px) {
          html body .wa-float-btn, html body .wa-tooltip { bottom: 90px !important; }
          html body #ai-chatbot-toggle { bottom: 160px !important; }
          html body .chat-panel { bottom: 230px !important; }
        }
        @media (max-width: 480px) {
          .detail-stats-row { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ══ HERO / GALLERY + SUMMARY ══════════════════════════════════════════ */}
      <div style={{ background: '#f4f7fb', paddingTop: '90px', paddingBottom: '64px', boxSizing: 'border-box' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px 0', boxSizing: 'border-box' }}>

          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', fontFamily: FONT, fontSize: '13px', color: '#6b7a8d' }}>
            <a href="/offplan" style={{ color: GOLD, textDecoration: 'none', fontWeight: 600 }}>Off-Plan</a>
            <span>›</span>
            <span style={{ color: NAVY, fontWeight: 600 }}>{p.name}</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '32px', alignItems: 'stretch' }} className="detail-hero-grid">

            {/* Gallery — stretches to match summary card height */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {p.gallery && p.gallery.length > 0 ? (
                <div style={{ flex: 1, minHeight: 0 }}>
                  <Gallery images={p.gallery} />
                </div>
              ) : (
                <div style={{ borderRadius: '14px', flex: 1, background: `linear-gradient(135deg, ${NAVY} 0%, #0a2d4a 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '380px' }}>
                  <span style={{ fontSize: '64px' }}>🏙️</span>
                </div>
              )}
            </div>

            {/* Summary card */}
            <div style={{ display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: '18px', padding: '28px', boxShadow: '0 8px 36px rgba(0,46,83,0.10)', border: '1px solid rgba(0,46,83,0.06)', position: 'sticky', top: '120px', height: '100%', boxSizing: 'border-box' }}>

              {/* Status badge */}
              {(() => {
                const STATUS_COLORS = { 'Available': '#22C55E', 'Now Selling': '#3B82F6', 'Coming Soon': '#F59E0B', 'Sold Out': '#EF4444' }
                const badgeColor = STATUS_COLORS[p.status] || p.statusColor || '#22C55E'
                return (
                  <div style={{ alignSelf: 'flex-start', display: 'inline-block', background: `${badgeColor}18`, color: badgeColor, fontFamily: FONT, fontSize: '11px', fontWeight: 700, padding: '4px 12px', borderRadius: '50px', letterSpacing: '0.5px', marginBottom: '14px', border: `1px solid ${badgeColor}30` }}>
                    {p.status}
                  </div>
                )
              })()}

              <h1 style={{ fontFamily: FONT, fontSize: 'clamp(20px, 2.5vw, 26px)', fontWeight: 800, color: NAVY, margin: '0 0 8px', lineHeight: 1.2 }}>
                {p.name}
              </h1>

              <div style={{ fontFamily: FONT, fontSize: '13px', color: '#6b7a8d', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px' }}>
                <span>📍</span>{p.area}
              </div>

              {/* Price */}
              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid rgba(0,46,83,0.07)' }}>
                <div style={{ fontFamily: FONT, fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>{t('offplanDetail.startingFrom')}</div>
                <div style={{ fontFamily: FONT, fontSize: '28px', fontWeight: 800, color: GOLD, lineHeight: 1 }}>{p.price?.replace('AED', '').replace('USD', '')}</div>
                {p.sizeLabel && (
                  <div style={{ fontFamily: FONT, fontSize: '12px', color: '#6b7a8d', marginTop: '4px' }}>{p.sizeLabel}</div>
                )}
              </div>

              {/* Key facts */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '11px', marginBottom: '22px' }}>
                {[
                  { icon: '👤', label: t('offplanExtra.developer'),      val: p.developer },
                  { icon: '📅', label: t('offplanDetail.handover'),       val: p.handover },
                  { icon: '🏗️', label: t('offplanDetail.propertyTypes'), val: propertyTypesStr },
                  // Per-type unit rows — one per type selected
                  ...Object.entries(unitOptionStrings).map(([type, str]) => ({
                    icon: type === 'Apartment' ? '🏢' : type === 'Townhouse' ? '🏘️' : type === 'Villa' ? '🏡' : type === 'Penthouse' ? '🌆' : '🛍️',
                    label: `${t(`offplanDetail.types.${type}`) !== `offplanDetail.types.${type}` ? t(`offplanDetail.types.${type}`) : type} ${t('offplanExtra.typesSuffix', 'Types')}`,
                    val: str,
                  })),
                  { icon: '🏠', label: t('offplanDetail.totalUnits'),    val: p.unitsCount ? `${p.unitsCount} ${t('offplanDetail.beds.units')}` : null },
                  { icon: '🛋', label: t('offplanExtra.furnishing'),     val: p.furnishing },
                  { icon: '📍', label: t('offplanExtra.community'),      val: p.community },
                  { icon: '📜', label: 'Permit Number', val: p.permitNumber },
                ].filter(r => r.val).map((row, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: FONT, fontSize: '12px', color: '#6b7a8d', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {row.icon} {row.label}
                    </span>
                    <span style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: NAVY, textAlign: 'right', maxWidth: '60%' }}>{row.val}</span>
                  </div>
                ))}
              </div>

              {/* Construction progress */}
              {p.constructionProgress !== null && p.constructionProgress !== undefined && (
                <div style={{ marginBottom: '22px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontFamily: FONT, fontSize: '12px', color: '#6b7a8d' }}>🏗 Construction Progress</span>
                    <span style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 700, color: GOLD }}>{p.constructionProgress}%</span>
                  </div>
                  <ProgressBar value={p.constructionProgress} />
                </div>
              )}

              {/* CTA Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: 'auto' }}>
                <button
                  onClick={() => setBrochureModalType('Brochure')}
                  style={{
                    display: 'block', width: '100%', textAlign: 'center',
                    background: 'transparent', color: NAVY, border: `2px solid ${NAVY}`,
                    fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                    padding: '12px', borderRadius: '10px', cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = `${NAVY}10` }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                >
                  📄 {t('offplanDetail.dlBrochure')}
                </button>
                <a
                  href="/contact"
                  id="offplan-detail-enquire"
                  style={{
                    display: 'block', textAlign: 'center',
                    background: NAVY, color: '#fff',
                    fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                    padding: '14px', borderRadius: '10px',
                    textDecoration: 'none', transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#00406e'}
                  onMouseLeave={e => e.currentTarget.style.background = NAVY}
                >
                  {t('props.enquireNow')}
                </a>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  id="offplan-detail-whatsapp"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    background: GOLD, color: '#fff',
                    fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                    padding: '13px', borderRadius: '10px',
                    textDecoration: 'none', transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#d4af6a'}
                  onMouseLeave={e => e.currentTarget.style.background = GOLD}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp Us
                </a>
                {p.youtubeLink && (
                  <a
                    href={p.youtubeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      background: '#f8fafc', color: NAVY, border: '1px solid #e2e8f0',
                      fontFamily: FONT, fontSize: '14px', fontWeight: 600,
                      padding: '13px', borderRadius: '10px',
                      textDecoration: 'none', transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9' }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc' }}
                  >
                    ▶ {t('offplanExtra.watchTour', 'Watch Property Tour')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ ABOUT & QUICK STATS ═════════════════════════════════════════════════ */}
      <Section badge={t('offplanDetail.overview')} title={`${t('offplanExtra.about')} ${p.name}`} id="about">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'start' }} className="detail-hero-grid">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {p.description ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                {p.description.split('\n').filter(t => t.trim() !== '').map((text, idx) => (
                  <p key={idx} style={{ fontFamily: FONT, fontSize: '15px', color: '#4a5568', lineHeight: 1.8, margin: 0 }}>
                    {text}
                  </p>
                ))}
              </div>
            ) : (
              <p style={{ fontFamily: FONT, fontSize: '15px', color: '#9ca3af', lineHeight: 1.8, margin: '0 0 24px', fontStyle: 'italic' }}>
                Contact our team for a full project briefing, including detailed availability, viewing schedules, and tailored investment analysis.
              </p>
            )}
            
            {p.investmentOutlook && (
                <div style={{ background: `${GOLD}10`, borderLeft: `4px solid ${GOLD}`, padding: '20px', borderRadius: '0 12px 12px 0', marginTop: '24px' }}>
                  <h4 style={{ fontFamily: FONT, fontSize: '14px', fontWeight: 700, color: NAVY, margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📈 {t('offplanExtra.investmentOutlook')}</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {p.investmentOutlook.split('\n').filter(t => t.trim() !== '').map((text, idx) => (
                      <p key={idx} style={{ fontFamily: FONT, fontSize: '14px', color: '#4a5568', lineHeight: 1.7, margin: 0 }}>
                        {text}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quick stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '14px', alignContent: 'start' }} className="detail-stats-row">
              {[
                { icon: '🏙️', label: t('offplanDetail.location'),        val: `${p.area}${p.emirate ? `, ${p.emirate}` : ''}` },
                { icon: '📅', label: t('offplanDetail.handover'),        val: p.handover },
                { icon: '📍', label: t('offplanExtra.community'),       val: p.community },
                { icon: '👤', label: t('offplanExtra.developer'),       val: p.developer },
                { icon: '🏗️', label: t('offplanDetail.propertyTypes'),  val: propertyTypesStr },
                { icon: '💰', label: t('offplanDetail.startingPrice'),  val: p.price },
                { icon: '🏠', label: t('offplanDetail.totalUnits'),     val: p.unitsCount ? `${p.unitsCount} ${t('offplanDetail.beds.units')}` : null },
                { icon: '💳', label: t('offplanDetail.paymentPlan'),    val: p.paymentPlanSummary },
                { icon: '🏢', label: t('offplanExtra.buildingHeight'), val: p.buildingHeight },
                { icon: '🛍️', label: t('offplanExtra.retailSpace'),   val: p.propertyTypes?.includes('Retail') ? p.retailSpace : null },
                { icon: '📐', label: t('offplanDetail.size'),           val: p.sizeLabel },
                // Per-type unit rows: "Apartment Types", "Townhouse Types", etc.
                ...Object.entries(unitOptionStrings).map(([type, str]) => ({
                  icon: type === 'Apartment' ? '🏢' : type === 'Townhouse' ? '🏘️' : type === 'Villa' ? '🏡' : type === 'Penthouse' ? '🌆' : '🏗️',
                  label: `${t(`offplanDetail.types.${type}`) !== `offplanDetail.types.${type}` ? t(`offplanDetail.types.${type}`) : type} ${t('offplanExtra.typesSuffix', 'Types')}`,
                  val: str,
                })),
              ].filter(s => s.val).map((stat, i) => (
                <div key={i} style={{ background: '#f4f7fb', borderRadius: '12px', padding: '16px' }}>
                  <div style={{ fontFamily: FONT, fontSize: '18px', marginBottom: '6px' }}>{stat.icon}</div>
                  <div style={{ fontFamily: FONT, fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px' }}>{stat.label}</div>
                  <div style={{ fontFamily: FONT, fontSize: '14px', fontWeight: 700, color: NAVY }}>{stat.val}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>

      {/* ══ AMENITIES ════════════════════════════════════════════════════════ */}
      {p.amenities && p.amenities.length > 0 && (
        <Section badge={t('offplanExtra.lifestyle')} title={t('offplanExtra.worldClassAmenities')} bg="#f4f7fb" id="amenities">
          <AmenitiesGrid amenities={p.amenities} />
        </Section>
      )}

      {/* ══ FLOOR PLANS ══════════════════════════════════════════════════════ */}
      {p.floorPlans && p.floorPlans.length > 0 && (
        <Section badge={t('offplanExtra.unitTypes')} title={t('offplanExtra.floorPlans')} id="floor-plans">
          <FloorPlans plans={p.floorPlans} areaUnit={p.areaUnit} currency={p.currency} onOpenBrochure={() => setBrochureModalType('Floor Plans')} />
        </Section>
      )}

      {/* ══ PAYMENT PLAN ════════════════════════════════════════════════════ */}
      {p.paymentPlan && p.paymentPlan.length > 0 && (
        <Section badge="Financing" title={t('offplanDetail.paymentPlan')} bg="#f4f7fb" id="payment-plan">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'start' }} className="detail-hero-grid">
            <PaymentTimeline steps={p.paymentPlan} />
            <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`, borderRadius: '18px', padding: '32px', color: '#fff' }}>
              <div style={{ fontFamily: FONT, fontSize: '13px', color: GOLD, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>💡 {t('offplanExtra.whyOffPlan', 'Why Off-Plan?')}</div>
              <ul style={{ fontFamily: FONT, fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 2, paddingLeft: '18px', margin: 0 }}>
                <li>{t('offplanExtra.lowerEntryPrice', 'Lower entry price vs ready properties')}</li>
                <li>{t('offplanExtra.spreadPayments', 'Spread payments over construction')}</li>
                <li>{t('offplanExtra.capitalAppreciation', 'Capital appreciation before handover')}</li>
                <li>{t('offplanExtra.zeroCommission', '0% commission — buy direct')}</li>
              </ul>
              <a href="/contact" style={{
                display: 'inline-block', marginTop: '24px',
                background: GOLD, color: '#fff', textDecoration: 'none',
                fontFamily: FONT, fontSize: '13px', fontWeight: 700,
                padding: '12px 28px', borderRadius: '10px', transition: 'background 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#d4af6a'}
                onMouseLeave={e => e.currentTarget.style.background = GOLD}
              >
                {t('offplanExtra.speakToAdvisor', 'Speak to an Advisor')} →
              </a>
            </div>
          </div>
        </Section>
      )}

      {/* ══ LOCATION & CONNECTIVITY ══════════════════════════════════════════ */}
      {(p.area || (p.nearbyPlaces && p.nearbyPlaces.length > 0)) && (
        <Section badge="Location" title={t('offplanDetail.location')} id="location">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px', alignItems: 'flex-start' }}>
            
            {/* The Map */}
            {(p.area || (p.lat && p.lng)) && (
              <div style={{ flex: '1 1 500px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,46,83,0.10)', border: '1px solid rgba(0,46,83,0.06)' }}>
                  <iframe
                    title={`Map — ${p.name}`}
                    width="100%"
                    height="450"
                    style={{ border: 'none', display: 'block' }}
                    loading="lazy"
                    src={(p.lat && p.lng) 
                      ? `https://maps.google.com/maps?q=${p.lat},${p.lng}&t=&z=15&ie=UTF8&iwloc=&output=embed`
                      : `https://maps.google.com/maps?q=${encodeURIComponent(p.name + ', ' + p.area + ', Dubai')}&t=&z=13&ie=UTF8&iwloc=&output=embed`
                    }
                  />
                </div>
                {(p.googleMapsLink || (p.lat && p.lng)) && (
                  <a href={p.googleMapsLink || `https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`} target="_blank" rel="noopener noreferrer" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    background: '#f8fafc', color: NAVY, textDecoration: 'none',
                    fontFamily: FONT, fontSize: '14px', fontWeight: 600,
                    padding: '12px 24px', borderRadius: '10px', border: '1px solid #e2e8f0',
                    transition: 'all 0.2s', alignSelf: 'flex-start'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = NAVY; e.currentTarget.style.color = '#fff' }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = NAVY }}
                  >
                    📍 {t('offplanExtra.openMap', 'Open Exact Location in Google Maps')} ↗
                  </a>
                )}
              </div>
            )}

            {/* Nearby Places */}
            {p.nearbyPlaces && p.nearbyPlaces.length > 0 && (
              <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <h4 style={{ margin: '0 0 8px', fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY }}>{t('offplanExtra.nearbyPlaces', 'Nearby Places')}</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {p.nearbyPlaces.map((np, i) => (
                    <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '16px', border: '1px solid rgba(0,46,83,0.04)', boxShadow: '0 2px 12px rgba(0,0,0,0.02)', transition: 'transform 0.2s', cursor: 'default' }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <div style={{ background: '#f8fafc', width: '48px', height: '48px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🚗</div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY }}>{np.distance.replace('mins', t('offplanDetail.mins', 'mins'))}</span>
                        <span style={{ fontFamily: FONT, fontSize: '13px', color: '#6b7a8d' }}>{t('offplanExtra.to', 'to')} {np.place}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </Section>
      )}

      {/* ══ BOTTOM CTA ══════════════════════════════════════════════════════ */}
      <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 100%)`, padding: '64px 20px', boxSizing: 'border-box', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <span style={{ display: 'inline-block', background: `${GOLD}25`, color: GOLD, fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px', padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase', border: `1px solid ${GOLD}40`, marginBottom: '16px' }}>
            {t('offplanExtra.interested', 'Interested?')}
          </span>
          <h2 style={{ fontFamily: FONT, fontWeight: 800, fontSize: 'clamp(22px, 3vw, 34px)', color: '#fff', margin: '0 0 12px', lineHeight: 1.2 }}>
            {t('offplanExtra.readyToSecure', 'Ready to Secure Your Unit in')} {p.name}?
          </h2>
          <p style={{ fontFamily: FONT, fontSize: '15px', color: 'rgba(255,255,255,0.7)', margin: '0 0 32px', lineHeight: 1.7 }}>
            {t('offplanExtra.advisorGuide', 'Our specialist advisors can guide you through pricing, payment options, and the full buying process — at zero commission.')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <a href="/contact" style={{ background: GOLD, color: '#fff', fontFamily: FONT, fontWeight: 700, fontSize: '15px', padding: '15px 36px', borderRadius: '10px', textDecoration: 'none', transition: 'background 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#d4af6a'}
              onMouseLeave={e => e.currentTarget.style.background = GOLD}
            >
              {t('offplanExtra.getInTouch', 'Get in Touch')}
            </a>
            <a href={waLink} target="_blank" rel="noopener noreferrer" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', fontFamily: FONT, fontWeight: 600, fontSize: '15px', padding: '15px 36px', borderRadius: '10px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', transition: 'background 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            >
              💬 {t('offplanExtra.whatsapp', 'WhatsApp')}
            </a>
          </div>
        </div>
      </div>

      {/* ══ STICKY BOTTOM CTA (mobile / scroll) ══════════════════════════════ */}
      {stickyVisible && (
        <div className="mobile-sticky-bar" style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
          background: '#fff', padding: '14px 20px',
          boxShadow: '0 -4px 24px rgba(0,46,83,0.14)',
          display: 'flex', gap: '10px', alignItems: 'center',
          borderTop: `3px solid ${GOLD}`,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
            <div style={{ fontFamily: FONT, fontSize: '12px', color: GOLD, fontWeight: 600 }}>{p.price}</div>
          </div>
          <a href="/contact" style={{ background: NAVY, color: '#fff', fontFamily: FONT, fontWeight: 700, fontSize: '13px', padding: '10px 20px', borderRadius: '8px', textDecoration: 'none', whiteSpace: 'nowrap' }}>{t('offplanExtra.enquire', 'Enquire')}</a>
          <a href={waLink} target="_blank" rel="noopener noreferrer" style={{ background: '#25D366', color: '#fff', fontFamily: FONT, fontWeight: 700, fontSize: '13px', padding: '10px 16px', borderRadius: '8px', textDecoration: 'none' }}>💬</a>
        </div>
      )}

      <Footer />

      {brochureModalType && <BrochureModal project={p} type={brochureModalType} onClose={() => setBrochureModalType(null)} />}
    </div>
  )
}
